import { IRootScopeService } from 'angular';
import { Cesium } from 'app/cesium';
import { UrlInitParams } from 'app/main/url_init_params.service';
import { CachedEventRegistry } from 'app/shared/events/cached_event_registry';
import { Clock } from 'app/shared/time/clock';
import { ServerTimeOffsetService } from 'app/shared/time/server_time_offset.service';
import { Timeline } from 'app/shared/time/timeline';
import { julianToUnix } from 'app/shared/utilities/time';
import $ from 'jquery';
import moment, { Moment } from 'moment-timezone';
import CesiumTimeThrottledEventRegistry from '../events/cesium_time_throttled_event_registry';
import { ClockHandlerFSM } from './clock_handler_fsm';
import { UserGeneratedConfigService } from 'app/config/user_generated_config.service';
import { ConfigService } from 'app/config/config.service';

export class ClockHandler {
    private readonly _clockHandlerFSM: ClockHandlerFSM;
    private readonly _$rootScope: IRootScopeService;
    private readonly _userGeneratedConfigService: UserGeneratedConfigService;
    private readonly _serverTimeOffsetService: ServerTimeOffsetService;
    private readonly _clock: Clock;
    private readonly _timeline: Timeline;
    private readonly _playSpeedChangedEvent: CachedEventRegistry;
    private readonly _liveChangedEvent: CachedEventRegistry;
    private readonly _lockedChangedEvent: CachedEventRegistry;
    private readonly _playChangedEvent: CachedEventRegistry;
    private readonly _cursorMoveEvent: CesiumTimeThrottledEventRegistry;
    private readonly _timelineMoveEvent: CesiumTimeThrottledEventRegistry;
    private readonly _timelineMinutes: number;
    private readonly _streamMargin: number;
    private _liveLatency: number;
    private readonly _liveToleranceSeconds: number;

    private _locked: boolean;
    private _previousSecondTick: Cesium.JulianDate;
    private _cachedCursorUnixTime: number;

    /**
     * Responsible for managing the state of the clock and nudging the timeline back and forward.
     * Handles live mode and is the source of truth for most of the state of time!
     * @param clockHandlerFSM
     * @param $rootScope
     * @param userGeneratedConfigService
     * @param serverTimeOffsetService
     * @param clock
     * @param timeline
     * @param params
     * @param timelineMinutes
     * @param streamMargin
     * @param liveToleranceSeconds
     * @param latestMomentFactory
     */
    constructor(
        clockHandlerFSM: ClockHandlerFSM,
        $rootScope: IRootScopeService,
        userGeneratedConfigService: UserGeneratedConfigService,
        serverTimeOffsetService: ServerTimeOffsetService,
        clock: Clock,
        timeline: Timeline,
        params: UrlInitParams,
        timelineMinutes: number = 30,
        streamMargin: number = 20,
        liveToleranceSeconds: number = 20,
        latestMomentFactory: () => Moment = undefined, // just for testing,
    ) {
        if (!clockHandlerFSM) {
            throw new Error('clockHandlerFSM must be defined');
        }

        // TODO only for testing! :(
        if (latestMomentFactory) {
            this.latestMoment = latestMomentFactory.bind(this);
        }

        this._clockHandlerFSM = clockHandlerFSM;
        this._$rootScope = $rootScope;
        this._userGeneratedConfigService = userGeneratedConfigService;
        this._serverTimeOffsetService = serverTimeOffsetService;
        this._locked = false;
        this._timelineMinutes = timelineMinutes;
        this._streamMargin = streamMargin;
        this._clock = clock;
        this._timeline = timeline;
        this._liveLatency = null;
        this._liveToleranceSeconds = liveToleranceSeconds;
        this._playSpeedChangedEvent = new CachedEventRegistry();
        this._liveChangedEvent = new CachedEventRegistry();
        this._lockedChangedEvent = new CachedEventRegistry();
        this._playChangedEvent = new CachedEventRegistry();
        this._cursorMoveEvent = new CesiumTimeThrottledEventRegistry();
        this._timelineMoveEvent = new CesiumTimeThrottledEventRegistry();
        this._clockHandlerFSM.addActionListener(this._actionHandler.bind(this));

        const setLiveLatency = (config) => {
            this._liveLatency = config.liveLatency;
        };

        this._userGeneratedConfigService.onConfigLoad((config) => {
            return setLiveLatency(config);
        });

        this._userGeneratedConfigService.onConfigChange((config) => {
            return setLiveLatency(config);
        });

        // init the clock to play so it is consistent with the state machine
        this._clock.play();

        // Get the initial state
        const [live, start, end, cursor] = ClockHandler.getInitialState(
            params.isLive,
            params.timelineEndMoment,
            params.selectedTimeMoment,
            this.latestMoment(),
            this._timelineMinutes,
            this._streamMargin,
        );

        this._clock.addTickListener(this._frameTick.bind(this));
        this._timeline.addSetTimeListener(this._frameTick.bind(this));

        // Add a listener to input events into our state machine
        this._serverTimeOffsetService.onChange(() => {
            this._clockHandlerFSM.onServerTimeOffsetChanged();
        });

        // Need to start the fsm before a potential call to toggle play pause
        // which ends up triggering an event
        this._clockHandlerFSM.start();

        // if we are not live we probably have been passed some url parameters to set the
        // time line position and cursor position
        if (!live) {
            this.togglePlayPause();
            this._clock.setStartTime(start);
            this._clock.setEndTime(end);
            this._clock.setCursorTime(cursor);
            this._onTimelineChange();
        }

        this._previousSecondTick = new Cesium.JulianDate();
    }

    /**
     * Gets the timeline start time as a javascript Date object
     */
    getStartTimeAsDate() {
        return this._clock.getStartTimeAsDate();
    }

    /**
     * Gets the timeline end time as a javascript Date object
     */
    getEndTimeAsDate() {
        return this._clock.getEndTimeAsDate();
    }

    /**
     * Get the end time of the timeline as a moment. Try and refactor clients of
     * clock handler to use this instead of directly accessing the clock
     * @returns a moment
     */
    getStartTimeAsMoment() {
        // we would normally clone this but there's no need to as inside
        // of clock a new moment is created.
        return this._clock.getStartTimeAsMoment();
    }

    /**
     * Get the end time of the timeline as a moment. Try and refactor clients of
     * clock handler to use this instead of directly accessing the clock
     * @returns a moment
     */
    getEndTimeAsMoment() {
        // we would normally clone this but there's no need to as inside
        // of clock a new moment is created.
        return this._clock.getEndTimeAsMoment();
    }

    /**
     * Get the cursor time of the timeline as a moment. Try and refactor clients of
     * clock handler to use this instead of directly accessing the clock
     * @returns a moment
     */
    getCursorTimeAsMoment() {
        // we would normally clone this but there's no need to as inside
        // of clock a new moment is created.
        return this._clock.getCursorTimeAsMoment();
    }

    /**
     * Get the start time of the timeline as a julian date. Try and refactor clients of
     * clock handler to use this instead of directly accessing the clock
     * @returns a julian
     */
    getStartTimeAsJulian() {
        // Need to clone. We don't want to mess with the cesium clock's copy
        const julian = this._clock.getStartTimeAsJulian();
        return julian ? julian.clone() : null;
    }

    /**
     * Get the end time of the timeline as a julian date. Try and refactor clients of
     * clock handler to use this instead of directly accessing the clock
     * @returns a julian
     */
    getEndTimeAsJulian() {
        // Need to clone. We don't want to mess with the cesium clock's copy
        const julian = this._clock.getEndTimeAsJulian();
        return julian ? julian.clone() : null;
    }

    /**
     * Get the cursor time as a julian date.
     * @returns a julian
     */
    getCursorTimeAsJulian(): Cesium.JulianDate {
        // Need to clone. We don't want to mess with the cesium clock's copy
        const julian = this._clock ? this._clock.getCursorTimeAsJulian() : null;
        return julian ? julian.clone() : null;
    }

    getCursorTimeAsUnix(): number {
        return this._cachedCursorUnixTime;
    }

    get timelineMinutes() {
        return this._timelineMinutes;
    }

    /**
     * The stream margin is the number of seconds from the edge of the timeline to the cursor
     * that we will start to nudge the timeline forwards or backwards.
     * @returns {number|*} stream margin in seconds
     */
    get streamMargin() {
        return this._streamMargin;
    }

    /**
     * Timeline object getter
     * @returns {null|*}
     */
    get timeline() {
        return this._timeline;
    }

    /**
     * Clock object getter.
     * WARNING: DO NOT RUN WRITE OPERATIONS ON THE CLOCK FROM OUTSIDE OF THIS OBJECT
     * eventually we should remove the ability of anything outside of the object to have
     * direct access to the clock object.
     *
     * The ClockHandler is responsible for arbitrating write operations to the clock object.
     * @returns {null|*} Clock object
     */
    get clock() {
        return this._clock;
    }

    /**
     * Registers a function to be called back when the play speed changes
     *
     * listenerFunc(newPlaySpeed) {}
     *
     * @param listener function
     * @returns {function()} listener destroyer function
     */
    addOnPlaySpeedChangedListener(listener) {
        return this._playSpeedChangedEvent.addEventListener(listener);
    }

    /**
     * Registers a function to be called back when the live state changes
     *
     * listenerFunc(newLiveState) {}
     *
     * @param listener listener function
     * @returns {function()} listener destroyer function
     */
    addOnLiveChangedListener(listener) {
        return this._liveChangedEvent.addEventListener(listener);
    }

    /**
     * Registers a function to be called back when the clock becomes locked/unlocked
     *
     * listenerFunc(newLockedState) {}
     *
     * @param listener listener function
     * @returns {function()} listener destroyer function
     */
    addOnLockedChangedListener(listener) {
        return this._lockedChangedEvent.addEventListener(listener);
    }

    /**
     * Registers a function to be called back when the play state changes
     *
     * listenerFunc(newPlayState) {]
     *
     * @param listener
     * @returns {function()} listener destroyer function
     */
    addOnPlayChangedListener(listener) {
        return this._playChangedEvent.addEventListener(listener);
    }

    addOnTickListener(listener) {
        return this._clock.addTickListener(listener);
    }

    /**
     * Register a function to be called every time the cursor moves by a certain number of seconds
     * @param listener The function to be invoked with the current cursor time as a moment, and a julian
     * @param secondsThrottle How many seconds the cursor must move between calls to the listener
     * @returns {function()} Call me to stop listening
     */
    addOnCursorMovedListener(
        listener: (
            currentJulianTime: Cesium.JulianDate,
            currentMoment: Moment,
            currentUnixTime: number,
        ) => void,
        secondsThrottle: number,
    ): () => void {
        return this._cursorMoveEvent.addEventListener(listener, secondsThrottle);
    }

    /**
     * Register a function to be called every time the timeline moves by a certain number of seconds
     * @param listener The function to be invoked with the current start and end times as moments and as julians
     * @param secondsThrottle How many seconds the timeline must move between calls to the listener
     * @returns {function()} Call me to stop listening
     */
    addOnTimelineMovedListener(
        listener: (
            startJulian: Cesium.JulianDate,
            endJulian: Cesium.JulianDate,
            cursorJulian: Cesium.JulianDate,
            startMoment: Moment,
            endMoment: Moment,
            cursorMoment: Moment,
        ) => any,
        secondsThrottle: number = 1,
    ) {
        return this._timelineMoveEvent.addEventListener(listener, secondsThrottle);
    }

    /**
     * Are we currently in live mode?
     * @returns {boolean}
     */
    isLive() {
        return this._clockHandlerFSM.getStateName() === ClockHandlerFSM.LIVE_STATE;
    }

    public isPaused(): boolean {
        return this._clockHandlerFSM.getStateName() === ClockHandlerFSM.PAUSED_STATE;
    }

    /**
     * Is the service not allowing any changes to the clock?
     * @returns {boolean}
     */
    isLocked() {
        return this._locked;
    }

    /**
     * Set whether changes to the clock should be not allowed
     * @param {boolean} boolean
     */
    setLocked(boolean) {
        if (this._locked !== boolean) {
            this._lockedChangedEvent.notifyListeners(boolean);
            this._locked = boolean;

            // TODO what the heck is this doing here
            if (boolean) {
                $('.cesium-timeline-main').css({
                    'pointer-events': 'none',
                    opacity: 0.4,
                });
            } else {
                $('.cesium-timeline-main').css({
                    'pointer-events': 'initial',
                    opacity: 'initial',
                });
            }
        }
    }

    /**
     * Requests the play / pause to be toggled. Consider registering for the appropriate listener
     * to know when the changes goes through. It is not guaranteed the changes will be accepted.
     */
    togglePlayPause() {
        if (!this._locked) {
            this._clockHandlerFSM.onTogglePlayPause();
        }
    }

    /**
     * Requests the play speed to be set to a number. Consider registering for the appropriate
     * listener to know when the changes goes through. It is not guaranteed the changes will be
     * accepted.
     * @param playSpeed
     */
    setPlaySpeed(playSpeed) {
        if (!this._locked) {
            this._clockHandlerFSM.onSetPlayMultiplier(playSpeed);
        }
    }

    /**
     * Moves the time cursor left or right by a given number of seconds
     * @param seconds postive for right movement, negative for left movement
     */
    moveCursor(seconds) {
        if (!this._locked) {
            this._clock.setCursorTime(this._clock.getCursorTimeAsMoment().add(seconds, 'seconds'));
            this._frameTick();
        }
    }

    /**
     * Moves the time cursor to the specified time, only moving the timeline if necessary to keep the cursor contained
     * @param cursorMoment The new cursor time as a moment
     * @param pause Whether to pause the timeline after moving
     */
    moveCursorToMoment(cursorMoment, pause) {
        if (!this._locked) {
            this._clock.setCursorTime(cursorMoment);
            this._frameTick();
            if (pause && this._clock.isPlaying()) {
                this.togglePlayPause();
            }
        }
    }

    /**
     * Request the timeline is moved to accommodate a new cursor time. The timeline end is set
     * streamMargin seconds after the cursor. The start time is set timelineMinutes before
     * the timeline end
     * @param cursorMoment new cursor time as a moment
     */
    moveTimeline(cursorMoment) {
        if (!this._locked) {
            this._clock.setStartTime(
                cursorMoment
                    .clone()
                    .subtract(this._timelineMinutes, 'minutes')
                    .add(this._streamMargin, 'seconds'),
            );
            this._clock.setEndTime(cursorMoment.clone().add(this._streamMargin, 'seconds'));
            this._clock.setCursorTime(cursorMoment);

            // moving with the date picker is equivalent to scrubbing the time cursor
            this._clockHandlerFSM.onScrubExitLive();

            this._onTimelineChange();
        }
    }

    /**
     * Request the timeline is moved to accommodate a new cursor time. The cursor will be placed in the
     * center of the timeline and the timeline will be adjusted accordingly
     * @param cursorMoment new cursor time as a moment
     */
    moveCursorToCentralTimeLine(cursorMoment) {
        if (!this._locked) {
            this._clock.setStartTime(
                cursorMoment
                    .clone()
                    .subtract(this._timelineMinutes / 2, 'minutes')
                    .add(this._streamMargin, 'seconds'),
            );
            this._clock.setEndTime(
                cursorMoment
                    .clone()
                    .add(this._timelineMinutes / 2, 'minutes')
                    .add(this._streamMargin, 'seconds'),
            );
            this._clock.setCursorTime(cursorMoment);

            // moving with the date picker is equivalent to scrubbing the time cursor
            this._clockHandlerFSM.onScrubExitLive();

            this._onTimelineChange();
        }
    }

    /**
     * handle the centralize cursor in timeline based on conditions
     * only center the cursor in timeline if a time outside the current timeframe is selected
     * @param unixTimeStamp new cursor time as unixTimeStamp
     */
    handleCentralizeTimelineCursor(unixTimeStamp) {
        if (ConfigService.centralizeTimelineCursor) {
            if (
                unixTimeStamp >= this.getStartTimeAsDate().getTime() &&
                unixTimeStamp <= this.getEndTimeAsDate().getTime()
            ) {
                this.moveCursorToMoment(moment(new Date(unixTimeStamp)), false);
            } else {
                this.moveCursorToCentralTimeLine(moment(new Date(unixTimeStamp)));
            }
        } else {
            this.moveCursorToMoment(moment(new Date(unixTimeStamp)), false);
        }
    }

    /**
     * Request live state to be toggled. Consider registering for the appropriate event to know
     * if / when live state changes. It is not guaranteed that the toggle will be accepted.
     */
    toggleLive() {
        if (!this._locked) {
            this._clockHandlerFSM.onToggleLive();
        }
    }

    _setLive() {
        this._clock.setStartTime(this.latestMoment().subtract(this._timelineMinutes, 'minutes')); // Move the start to the latest moment - 30 minutes
        this._clock.setEndTime(this.latestMoment()); // Move the end to the latest moment
        this._clock.setCursorTime(this._perfectLiveTime); // Move the cursor to the latest moment - streaming margin
        this._clock.playSpeed = 1;
        this._playSpeedChangedEvent.notifyListeners(1);
        this._liveChangedEvent.notifyListeners(true);
    }

    _disableLive() {
        // HACK, add this microtask to the end of the queue
        //  because the onExit is executed BEFORE the state has actually changed
        //  so if any listener of the live event immediately asks if the clock handler is live, then it will still be
        //  true as the state machine hasn't technically changed state yet; it's still in the process of exiting!
        Promise.resolve().then(() => {
            return this._liveChangedEvent.notifyListeners(false);
        });
    }

    get _perfectLiveTime() {
        return Cesium.JulianDate.fromDate(
            this.latestMoment().subtract(this._streamMargin, 'seconds').toDate(),
        );
    }

    latestMoment() {
        return moment()
            .subtract(this._liveLatency, 'seconds')
            .add(this._serverTimeOffsetService.secondsServerIsAheadOfClient, 'seconds');
    }

    _actionHandler(actionName, arg0) {
        switch (actionName) {
            case ClockHandlerFSM.PAUSE_ACTION:
                this._clock.pause();
                this._playChangedEvent.notifyListeners(false);
                break;
            case ClockHandlerFSM.PLAY_ACTION:
                this._clock.play();
                this._playChangedEvent.notifyListeners(true);
                break;
            case ClockHandlerFSM.LIVE_ACTION:
                this._setLive();
                break;
            case ClockHandlerFSM.SET_CLOCK_MULTIPLIER_ACTION:
                this._clock.playSpeed = arg0;
                this._playSpeedChangedEvent.notifyListeners(arg0);
                break;
            case ClockHandlerFSM.DISABLE_LIVE_ACTION:
                this._disableLive();
                break;
            default:
                console.log('WARNING: Clock handler action not handled', actionName, arg0);
                break;
        }
    }

    _secondTick() {
        this._cursorMoveEvent.notifyListeners(
            this.getCursorTimeAsJulian(),
            this.getCursorTimeAsMoment(),
            this.getCursorTimeAsUnix(),
        );

        const currentCursorTime = this._clock.getCursorTimeAsJulian();
        const perfectLiveTime = this._perfectLiveTime;

        const secondsCursorIsBehindLive = Cesium.JulianDate.secondsDifference(
            perfectLiveTime,
            currentCursorTime,
        );
        const isCursorInLiveZone =
            Math.abs(secondsCursorIsBehindLive) <= this._liveToleranceSeconds;
        const cursorIsAheadOfLiveZone = secondsCursorIsBehindLive < -this._liveToleranceSeconds;

        // Not so nice of handling event input into our StateMachine
        // Although we are matching on the boolean isLive, this does not mean that the
        // underlying state machine will determine us to be in live mode!
        // We only use it as a means to work our what sort of event we should be firing into
        // the state machine
        if (this._timeline.isScrubbing()) {
            if (isCursorInLiveZone) {
                this._clockHandlerFSM.onScrubEnterLive();
            } else {
                this._clockHandlerFSM.onScrubExitLive();
            }
        } else {
            if (isCursorInLiveZone) {
                this._clockHandlerFSM.onPlayEnterLive(this._clock.playSpeed);
            } else {
                this._clockHandlerFSM.onPlayExitLive();
            }
        }

        // Only bump the cursor if the state machine has determined that we should be in live mode,
        // or if we're not in live mode but too far ahead of live time.
        if (this.isLive() || cursorIsAheadOfLiveZone) {
            this._clock.setCursorTime(perfectLiveTime);
        }

        const initialStart = this._clock.getStartTimeAsJulian();
        const initialEnd = this._clock.getEndTimeAsJulian();
        const maxEndTime = Cesium.JulianDate.fromDate(this.latestMoment().toDate());

        // Get the new timeline positions (automatic pushing of the timeline)
        const [start, end] = ClockHandler.recalculateTimeline(
            this._clock.getCursorTimeAsJulian(),
            initialStart,
            initialEnd,
            this._streamMargin,
            maxEndTime,
        );

        const timelineChanged = !start.equals(initialStart) || !end.equals(initialEnd);

        if (timelineChanged) {
            this._clock.setStartTime(start);
            this._clock.setEndTime(end);

            this._onTimelineChange();
        }
    }

    /**
     * Called when the timeline changes (eg start/end time). Won't be called if the cursor changes but not timeline
     */
    private _onTimelineChange() {
        let startTimeAsJulian = this.getStartTimeAsJulian();
        let endTimeAsJulian = this.getEndTimeAsJulian();
        let startTimeAsMoment = this.getStartTimeAsMoment();
        if (!startTimeAsJulian || !startTimeAsMoment || !endTimeAsJulian) {
            return;
        }

        // Tell the timeline to look at the new clock positions.
        this._timeline.lookAt(startTimeAsJulian, endTimeAsJulian);

        // The first argument to notifyListeners must be startTimeAsJulian,
        // as it is required by the CesiumTimeThrottledEventRegistry
        this._timelineMoveEvent.notifyListeners(
            startTimeAsJulian,
            endTimeAsJulian,
            this.getCursorTimeAsJulian(),
            startTimeAsMoment,
            this.getEndTimeAsMoment(),
            this.getCursorTimeAsMoment(),
        );
    }

    /**
     * Force a tick of the timeline, this should be called anytime to update the state machine and all the internals.
     * It has a internal throttle of 1 second from the last clock time.
     * This method is/should be called almost every frame!
     */
    _frameTick() {
        // every frame, just cache the current julian time as a unix to save converting multiple times per frame
        this._cachedCursorUnixTime = julianToUnix(this._clock.getCursorTimeAsJulian());
        const secondsPassed = Math.abs(
            Cesium.JulianDate.secondsDifference(
                this._clock.getCursorTimeAsJulian(),
                this._previousSecondTick,
            ),
        );
        if (secondsPassed >= 1) {
            this._previousSecondTick = this._clock.getCursorTimeAsJulian();
            this._secondTick();
        }
    }

    /**
     * Takes the current timeline start and end. Will move the timeline in one direction or another
     * based on the position of the cursor
     *
     * Made a public static for testability
     *
     * @param cursor julian time
     * @param start julian time
     * @param end julian time
     * @param padding padding in seconds from the end of the timeline
     * @param maxEndTime Makes sure the timeline doesn't extends past this time
     * @returns {[*,*,boolean]}
     */
    static recalculateTimeline(cursor, start, end, padding, maxEndTime) {
        const { JulianDate } = Cesium;
        const { secondsDifference, addSeconds, greaterThan } = JulianDate;
        // This is where the logic is stored for shuffling the timeline along (streaming, if you will).
        // You give it the current cursor position, start time and end time, and it will return you a new start time and end time.
        // Currently if will always give a start and end time where the cursor is inside a the 'padding'.
        if (greaterThan(start, end)) {
            throw new Error('Start time can not be after end time');
        }
        const paddedStart = addSeconds(start, padding, new JulianDate());
        const paddedEnd = addSeconds(end, -padding, new JulianDate());
        const distCursorIsFromEndPad = secondsDifference(cursor, paddedStart);
        const distCursorIsFromStartPad = secondsDifference(cursor, paddedEnd);
        const distEndTimeIsOverMaxEndTime = secondsDifference(end, maxEndTime);
        const distCursorWantsToMoveTimeline =
            distCursorIsFromStartPad > 0
                ? distCursorIsFromStartPad
                : distCursorIsFromEndPad < 0
                  ? distCursorIsFromEndPad
                  : 0;
        const secondsToMoveTimelineBy =
            distEndTimeIsOverMaxEndTime > 0
                ? -distEndTimeIsOverMaxEndTime
                : distCursorWantsToMoveTimeline;
        const newStart = addSeconds(start, secondsToMoveTimelineBy, new JulianDate());
        const newEnd = addSeconds(end, secondsToMoveTimelineBy, new JulianDate());

        // Otherwise return the start and end times
        return [newStart, newEnd];
    }

    /**
     * Calculates an initial state of the timeline based on parameters that may or may not be null
     *
     * Made a public static for testability
     *
     * @param live boolean
     * @param end moment
     * @param cursor moment
     * @param latest moment
     * @param timelineMinutes seconds as integer
     * @param streamMargin seconds as integer
     * @returns {[*,*,*,*]}
     */
    static getInitialState(live, end, cursor, latest, timelineMinutes, streamMargin) {
        const newLive = live;
        const newEnd = end || latest;
        const newStart = newEnd.clone().subtract(timelineMinutes, 'minutes');
        const newCursor = cursor || newEnd.clone().subtract(streamMargin, 'seconds');
        return [newLive, newStart, newEnd, newCursor];
    }
}
